import React, { useRef, useCallback, useState } from 'react';
import {
  useLessonApi,
  toResumeOffer,
  ResumeExpiredError,
  type ResumeOffer,
  type RunMode,
  type StartLessonResponse,
} from './useLessonApi';
import { useLessonState } from './useLessonState';
import { useAutoContinue } from './useAutoContinue';
import {
  handleProvideContextStep,
  handlePlayAudioStep,
  handleRequestProductionStep,
  handleGiveFeedbackStep,
  handleExplainMeaningStep,
  handleSceneBeatStep,
  handleFallbackStep,
  phraseHintsFor,
  awaitsLearnerInput,
  type Step,
} from '../utils/stepHandlers';
import { type ASRResult, type LearnedPhrase } from './useLessonApi';
import type { EarlierPhrase } from '../turncard/rail';
import {
  isAbortError,
  applyHandleNextStepError,
  runAutoContinueWithErrorClearing,
} from '../utils/lessonFlowUtils';

// Guard function to check if already started
const checkAlreadyStarted = (
  hasStartedRef: React.MutableRefObject<boolean>
): boolean => {
  return hasStartedRef.current;
};

// Guard function to check if already advancing
const checkAlreadyAdvancing = (
  isAdvancingRef: React.MutableRefObject<boolean>
): boolean => {
  return isAdvancingRef.current;
};

// Re-export shared utilities from lessonFlowUtils for backward compatibility
export {
  isAbortError,
  applyHandleNextStepError,
  runAutoContinueWithErrorClearing,
} from '../utils/lessonFlowUtils';
export type {
  HandleNextStepErrorStateSlice,
  AutoContinueStateSlice,
} from '../utils/lessonFlowUtils';

/**
 * The phrases a resumed lesson had already learned, ready for the rail (#347). A phrase whose clip can't be
 * fetched is left out: the rail only lists phrases that can be replayed.
 */
const earlierPhrasesOf = async (
  learned: LearnedPhrase[] | undefined,
  api: ReturnType<typeof useLessonApi>,
  signal?: AbortSignal
): Promise<EarlierPhrase[]> => {
  const fetched = await Promise.all(
    (learned ?? []).map(async (l): Promise<EarlierPhrase | null> => {
      try {
        const audioUrl = await api.fetchAudioUrl(l.form_id, 'natural', signal);
        return {
          partId: l.part_id,
          phrase: {
            id: `resumed-${l.seq}`,
            targetText: l.text,
            gloss: l.gloss,
            audioUrl,
          },
        };
      } catch (err) {
        // Leaving the page is not a missing clip: let the abort end the resume
        if (isAbortError(err)) throw err;
        return null;
      }
    })
  );
  return fetched.filter((e): e is EarlierPhrase => e !== null);
};

/**
 * Hook that manages lesson flow logic:
 * - Starting lessons
 * - Advancing to next steps
 * - Coordinating step handlers
 * - Managing AbortController lifecycle
 */
export const useLessonFlow = (
  lessonId: string | undefined,
  state: ReturnType<typeof useLessonState>,
  api: ReturnType<typeof useLessonApi>,
  autoContinue: ReturnType<typeof useAutoContinue>,
  mode: RunMode = 'lesson'
): {
  /**
   * Opens the lesson: offers a resumable run when there is one, else starts. `skipResumeCheck` starts
   * a fresh run without asking (Restart).
   */
  startLesson: (
    signal?: AbortSignal,
    bypassGuard?: boolean,
    skipResumeCheck?: boolean
  ) => Promise<void>;
  /** The unfinished run the learner is being offered, until they pick Continue or Restart. */
  resumeOffer: ResumeOffer | null;
  /** The run on screen was resumed, so its intro was already seen. */
  resumed: boolean;
  /** Continue the offered run; falls back to a fresh start when the window has passed. */
  continueRun: (signal?: AbortSignal) => Promise<void>;
  /** Start a fresh run instead of the offered one (the server abandons the old run). */
  restartRun: (signal?: AbortSignal) => Promise<void>;
  handleNextStep: (
    runId: string,
    stepVersion: number,
    asrResult?: ASRResult
  ) => Promise<boolean>;
  abortControllerRef: React.MutableRefObject<AbortController | null>;
} => {
  const abortControllerRef = useRef<AbortController | null>(null);
  const [resumeOffer, setResumeOffer] = useState<ResumeOffer | null>(null);
  const [resumed, setResumed] = useState(false);

  const handleNextStep = useCallback(
    async (
      currentRunId: string,
      currentStepVersion: number,
      asrResult?: ASRResult
    ): Promise<boolean> => {
      // Prevent duplicate calls
      if (checkAlreadyAdvancing(state.isAdvancingRef)) {
        return false;
      }

      state.isAdvancingRef.current = true;
      state.setAdvanceFailed(false);
      state.setProcessing(true);

      try {
        const data = await api.nextStep(
          currentRunId,
          currentStepVersion,
          asrResult
        );

        if (data.completed) {
          state.setAwaitingInput(false);
          state.setLessonComplete(true);
          state.setProcessing(false);
          return true;
        }

        if (!data.step) {
          console.error('[useLessonFlow] No step data in next step response');
          // Nothing new to answer: leave the learner on the prompt they had.
          if (asrResult) state.setAwaitingInput(true);
          return false;
        }

        // Update step version for next call
        state.setStepVersion(data.step_version);
        state.setStepIndex(data.step.step_index);

        const actionType = data.step.action_type;
        const stepId = `step-${data.step.step_index}-${actionType}-${Date.now()}`;
        // Turn the mic off now, but on only once this step's messages are in
        // (the step handler is still running).
        const awaitsInput = awaitsLearnerInput(data.step as Step);
        if (!awaitsInput) state.setAwaitingInput(false);

        if (actionType === 'play_audio') {
          // Use step handler for play_audio
          // Use getCurrentMessages to ensure we have the latest messages
          const result = await handlePlayAudioStep(
            data.step as Step,
            stepId,
            state.getCurrentMessages(),
            api
          );

          if (result.messages) {
            state.setMessagesWithCheck((prev) => [
              ...prev,
              ...result.messages!,
            ]);
          }
        } else if (actionType === 'request_production') {
          // Use step handler for request_production
          const result = handleRequestProductionStep(data.step as Step, stepId);
          if (result.messages) {
            state.setMessagesWithCheck((prev) => [
              ...prev,
              ...result.messages!,
            ]);
          }
          const step = data.step as Step;
          state.setPhraseHints(
            phraseHintsFor(
              step.content,
              step.content?.form as { text?: string } | undefined
            )
          );
        } else if (actionType === 'give_feedback') {
          // Use step handler for give_feedback
          const result = await handleGiveFeedbackStep(
            data.step as Step,
            stepId
          );

          if (result.messages) {
            state.setMessagesWithCheck((prev) => [
              ...prev,
              ...result.messages!,
            ]);
          }

          // Auto-continue if successful, otherwise restore hints for retry
          if (result.needsAutoContinue && currentRunId && data.step_version) {
            await runAutoContinueWithErrorClearing({
              runId: currentRunId,
              nextStepVersion: data.step_version,
              state,
              autoContinue,
              handleNextStep,
              signal: abortControllerRef.current?.signal,
              logLabel: 'Error auto-continuing after feedback:',
            });
          } else if (!result.needsAutoContinue) {
            // Retry path: the retry card withholds the phrase until the help ladder
            // reaches it, so keep the hints the request carried for the next attempt
            // (GCP confidence boost) and replace them only when the card sends some.
            const formAttempted = (data.step as Step)?.content
              ?.form_attempted as { text?: string } | undefined;
            const retryHints = phraseHintsFor(
              (data.step as Step)?.content,
              formAttempted
            );
            if (retryHints) state.setPhraseHints(retryHints);
          }
        } else if (actionType === 'explain_meaning') {
          const result = handleExplainMeaningStep(data.step as Step, stepId);
          if (result.messages) {
            state.setMessagesWithCheck((prev) => [
              ...prev,
              ...result.messages!,
            ]);
          }
          if (result.needsAutoContinue && currentRunId && data.step_version) {
            await runAutoContinueWithErrorClearing({
              runId: currentRunId,
              nextStepVersion: data.step_version,
              state,
              autoContinue,
              handleNextStep,
              signal: abortControllerRef.current?.signal,
              logLabel: 'Error auto-continuing after explain_meaning:',
            });
          }
        } else if (actionType === 'scene_beat') {
          const result = handleSceneBeatStep(data.step as Step, stepId);
          if (result.messages) {
            state.setMessagesWithCheck((prev) => [
              ...prev,
              ...result.messages!,
            ]);
          }
          if (result.needsAutoContinue && currentRunId && data.step_version) {
            await runAutoContinueWithErrorClearing({
              runId: currentRunId,
              nextStepVersion: data.step_version,
              state,
              autoContinue,
              handleNextStep,
              signal: abortControllerRef.current?.signal,
              logLabel: 'Error auto-continuing after scene_beat:',
            });
          }
        } else if (actionType === 'provide_context') {
          // provide_context can come from startLesson (first step) or from /next (first action of a newly advanced step)
          const result = await handleProvideContextStep(
            data.step as Step,
            stepId,
            currentRunId,
            data.step_version,
            state.getCurrentMessages(),
            api,
            abortControllerRef.current?.signal
          );

          if (result.error) {
            state.setError(result.error);
            return false;
          }

          if (result.messages) {
            state.setMessagesWithCheck((prev) => [
              ...prev,
              ...result.messages!,
            ]);

            if (result.needsAutoContinue && result.newStepVersion) {
              await new Promise((resolve) => setTimeout(resolve, 200));
            }
          }

          if (result.needsAutoContinue) {
            const nextStepVersion = result.newStepVersion ?? data.step_version;
            if (currentRunId && nextStepVersion) {
              await runAutoContinueWithErrorClearing({
                runId: currentRunId,
                nextStepVersion,
                state,
                autoContinue,
                handleNextStep,
                signal: abortControllerRef.current?.signal,
                logLabel: 'Error auto-continuing after provide_context:',
              });
            }
          }
        } else {
          // Use step handler for fallback
          const result = handleFallbackStep(data.step as Step, stepId);
          if (result.messages) {
            state.setMessagesWithCheck((prev) => [
              ...prev,
              ...result.messages!,
            ]);
          }
        }
        if (awaitsInput) state.setAwaitingInput(true);
        return true;
      } catch (err) {
        // A failed attempt leaves the learner on the same prompt: mic back on.
        if (asrResult) state.setAwaitingInput(true);
        applyHandleNextStepError(err, state);
        return false;
      } finally {
        state.isAdvancingRef.current = false;
        state.setProcessing(false);
      }
    },
    [api, state, autoContinue]
  );

  // Runs a start or resume response through the same handling: it is the next step to show either way.
  const beginRun = useCallback(
    async (
      fetchRun: () => Promise<StartLessonResponse>,
      signal?: AbortSignal
    ): Promise<void> => {
      state.hasStartedRef.current = true;

      try {
        state.setLoading(true);
        state.setError(null);
        setResumeOffer(null);

        const data = await fetchRun();

        state.setContextName(data.context_name);
        state.setContextId(data.context_id);
        state.setLanguageCode(data.language_code);
        state.setParts(data.parts ?? []);
        state.setEarlierPhrases(
          await earlierPhrasesOf(data.learned, api, signal)
        );
        state.setRunId(data.run_id);
        state.setStepVersion(data.step_version);
        if (data.step) {
          state.setStepIndex(data.step.step_index);
        }

        // A resume that ended the lesson goes straight to completion.
        if (!data.step && data.completed) {
          state.setAwaitingInput(false);
          state.setLessonComplete(true);
          return;
        }

        // Handle different action types from the backend
        if (!data.step) {
          // No step data - show error or fallback
          state.setMessages([
            {
              id: 'error-step',
              sender: 'tutor',
              content: 'Welcome to your lesson!',
              timestamp: new Date().toISOString(),
            },
          ]);
          return;
        }

        const actionType = data.step.action_type;
        const stepId = `step-${data.step.step_index}-${actionType}-${Date.now()}`;
        const awaitsInput = awaitsLearnerInput(data.step as Step);
        if (!awaitsInput) state.setAwaitingInput(false);

        if (actionType === 'provide_context') {
          // Use step handler for provide_context
          // Use getCurrentMessages to ensure we have the latest state (important for duplicate detection)
          const result = await handleProvideContextStep(
            data.step as Step,
            stepId,
            data.run_id,
            data.step_version,
            state.getCurrentMessages(),
            api,
            signal
          );

          if (result.error) {
            state.setError(result.error);
            state.setProcessing(false);
            return;
          }

          // Add messages if not duplicate
          if (result.messages) {
            state.setMessagesWithCheck((prev) => [
              ...prev,
              ...result.messages!,
            ]);

            // Wait a moment for state to update before continuing (if content was generated)
            if (result.needsAutoContinue && result.newStepVersion) {
              await new Promise((resolve) => setTimeout(resolve, 200));
            }
          }

          // Auto-continue to next step (play_audio)
          if (result.needsAutoContinue && data.run_id) {
            const nextStepVersion = result.newStepVersion || data.step_version;
            if (nextStepVersion) {
              await runAutoContinueWithErrorClearing({
                runId: data.run_id,
                nextStepVersion,
                state,
                autoContinue,
                handleNextStep,
                signal: abortControllerRef.current?.signal,
                logLabel: 'Error in handleNextStep:',
              });
            }
          }
        } else if (actionType === 'play_audio') {
          // Use step handler for play_audio
          const result = await handlePlayAudioStep(
            data.step as Step,
            stepId,
            state.getCurrentMessages(),
            api,
            signal
          );

          if (result.messages) {
            state.setMessages([...result.messages]);
          }
        } else if (actionType === 'request_production') {
          // Use step handler for request_production
          const result = handleRequestProductionStep(data.step as Step, stepId);
          if (result.messages) {
            state.setMessages([...result.messages]);
          }
          const step = data.step as Step;
          state.setPhraseHints(
            phraseHintsFor(
              step.content,
              step.content?.form as { text?: string } | undefined
            )
          );
        } else if (actionType === 'explain_meaning') {
          const result = handleExplainMeaningStep(data.step as Step, stepId);
          if (result.messages) {
            state.setMessages([...result.messages]);
          }
          if (result.needsAutoContinue && data.run_id && data.step_version) {
            await runAutoContinueWithErrorClearing({
              runId: data.run_id,
              nextStepVersion: data.step_version,
              state,
              autoContinue,
              handleNextStep,
              signal: abortControllerRef.current?.signal,
              logLabel:
                'Error auto-continuing after explain_meaning (startLesson):',
            });
          }
        } else if (actionType === 'scene_beat') {
          const result = handleSceneBeatStep(data.step as Step, stepId);
          if (result.messages) {
            state.setMessages([...result.messages]);
          }
          if (result.needsAutoContinue && data.run_id && data.step_version) {
            await runAutoContinueWithErrorClearing({
              runId: data.run_id,
              nextStepVersion: data.step_version,
              state,
              autoContinue,
              handleNextStep,
              signal: abortControllerRef.current?.signal,
              logLabel: 'Error auto-continuing after scene_beat (startLesson):',
            });
          }
        } else {
          // Use step handler for fallback
          const result = handleFallbackStep(data.step as Step, stepId);
          if (result.messages) {
            state.setMessages([...result.messages]);
          }
        }
        if (awaitsInput) state.setAwaitingInput(true);
      } catch (err) {
        // Don't set error if request was aborted
        if (isAbortError(err)) {
          return;
        }
        console.error('[useLessonFlow] Failed to start lesson:', err);
        state.setError(
          err instanceof Error ? err.message : 'Failed to start lesson'
        );
        // Don't reset flag on error - let retry handler reset it
        // This allows the guard to be testable while keeping retry functional
      } finally {
        // Only reset flag if not aborted and not auto-continuing
        // If we're auto-continuing, loading is already set to false and processing is true
        if (!signal?.aborted && !autoContinue.willAutoContinueRef.current) {
          state.setLoading(false);
        }
      }
    },
    [mode, api, state, autoContinue, handleNextStep]
  );

  const startLesson = useCallback(
    async (
      signal?: AbortSignal,
      bypassGuard = false,
      skipResumeCheck = false
    ): Promise<void> => {
      // A practice run starts from the learner's history, not from a lesson id.
      if (mode === 'lesson' && !lessonId) {
        state.setError('Lesson ID is required');
        state.setLoading(false);
        return;
      }

      // Prevent duplicate calls within the same mount
      // Note: StrictMode will cause remounts, but AbortController handles that
      // bypassGuard allows retry to work while still allowing guard to be tested
      if (!bypassGuard && checkAlreadyStarted(state.hasStartedRef)) {
        return;
      }

      state.hasStartedRef.current = true;
      setResumed(false);

      // Practice runs always start fresh. An unfinished lesson is offered before anything starts.
      if (mode === 'lesson' && !skipResumeCheck) {
        state.setLoading(true);
        state.setError(null);
        setResumeOffer(null);
        let offer: ResumeOffer | null = null;
        try {
          offer = toResumeOffer(await api.getResumable(lessonId ?? '', signal));
        } catch (err) {
          // The check is a convenience: if it fails, the lesson just starts.
          if (!isAbortError(err)) {
            console.warn('[useLessonFlow] Resumable check failed:', err);
          }
        }
        if (signal?.aborted) return;
        if (offer) {
          setResumeOffer(offer);
          state.setLoading(false);
          return;
        }
      }

      await beginRun(() => api.startLesson(lessonId ?? '', signal), signal);
    },
    [lessonId, mode, api, state, beginRun]
  );

  const continueRun = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      const offer = resumeOffer;
      if (!offer) return;
      await beginRun(async () => {
        try {
          const data = await api.resumeRun(offer.run_id, signal);
          setResumed(true);
          return data;
        } catch (err) {
          if (!(err instanceof ResumeExpiredError)) throw err;
          // Past the window: start over without asking again.
          return api.startLesson(lessonId ?? '', signal);
        }
      }, signal);
    },
    [resumeOffer, lessonId, api, beginRun]
  );

  const restartRun = useCallback(
    (signal?: AbortSignal): Promise<void> => startLesson(signal, true, true),
    [startLesson]
  );

  return {
    startLesson,
    resumeOffer,
    resumed,
    continueRun,
    restartRun,
    handleNextStep,
    abortControllerRef,
  };
};
